import type { FC } from 'react';
import { useEffect } from 'react';
import { toast } from 'sonner';
import { locationReload } from 'src/utils/location-usage';

// note: this is not an error boundary, because the kind of errors
// which are handled here happen outside of react
export const GlobalErrorHandler: FC = () => {
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      if (
        event.message ===
        'ResizeObserver loop completed with undelivered notifications.'
      ) {
        if (process.env.NODE_ENV !== 'test')
          console.warn(
            'Ignored "ResizeObserver loop completed with undelivered notifications." in global error event handler.',
            event
          );
        return;
      }

      if (process.env.NODE_ENV !== 'test')
        console.error('global error event handler', event);
      if (
        event.error instanceof TypeError &&
        event.error.message.startsWith(
          'Failed to fetch dynamically imported module'
        )
      ) {
        locationReload();
      } else {
        toast.error(
          <GlobalErrorToast
            message={event.error instanceof Error && event.error.message}
          />
        );
      }
    };

    const handlePromiseRejection = (event: PromiseRejectionEvent) => {
      if (process.env.NODE_ENV !== 'test')
        console.error('global unhandledrejection event handler', event);
      toast.error(
        <GlobalErrorToast
          message={typeof event.reason === 'string' && event.reason}
        />
      );
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handlePromiseRejection);
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handlePromiseRejection);
    };
    // oxlint-disable-next-line exhaustive-deps
  }, []);

  return null;
};

const GlobalErrorToast: FC<{ message?: string | false }> = ({ message }) => {
  return (
    <>
      <strong>Ooops!</strong>
      <br />
      <p>
        An unknown error happened. Our engineers have been informed. If this
        error persists, please get in touch so we can find a solution together.
      </p>
      <br />
      {message && <small>(Message: {message})</small>}
    </>
  );
};
